import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { from } from 'rxjs';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { WidgetInfo } from 'src/app/interfaces/widget-info';
import { WidgetPanelCounts } from 'src/app/interfaces/widget-panel-counts';
import { ToastService } from 'src/app/services/toast.service';
import { clientDashboardService } from 'src/app/shared/vive-api/client-dashboard.service';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

@Component({
  selector: 'app-accounting-alerts-widget',
  templateUrl: './accounting-alerts-widget.component.html',
  styleUrls: ['./accounting-alerts-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountingAlertsWidgetComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private Router: Router,
    private ToastService: ToastService
  ) {}

  @Input() CompanyId!: number;
  @Input() widgetInfo?: WidgetInfo;

  accountingAlertsMap: WidgetPanelCounts['data'] = new Map();

  navigateToGrid(displayCount: { [key: string]: any }) {
    const { query, route, FilterModel } = displayCount['value'];

    if (!route) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing route information.',
        { returnError: true }
      );
    }

    const state = { title: displayCount['key'] };

    if (FilterModel) {
      Object.assign(state, { FilterModel });
    }

    if (query) {
      Object.assign(state, { query });
    }

    return this.Router.navigate([route], { state });
  }

  isObjectWithCount(value: any): value is { count: number; query: {} } {
    return value && typeof value === 'object' && 'count' in value;
  }

  ngOnInit(): void {
    if (!this.widgetInfo) {
      throw new Error('Missing widget info.');
    }

    const clientVendorsRoute = `client/${this.CompanyId}/vendors/client`;
    const clientAdminAccountingRoute = `client/${this.CompanyId}/admin/accounting/apVendors`;

    const companyVendorsApUnlinkedCountsQuery = {
      viveStatus: { $notIn: ['Unregistered', 'Inactive'] },
      VendorId: { $ne: null },
      literals: {
        $and: 'apUnlinked'
      }
    };

    const getApVendorsAddedLast90DaysQuery: {
      ApConfig: GenericObject;
      createdAt: GenericObject;
    } = {
      ApConfig: {
        options: { required: true },
        attributes: []
      },
      createdAt: {
        $gte: (() => {
          const date90DaysAgo = new Date();
          date90DaysAgo.setDate(date90DaysAgo.getDate() - 90);

          return date90DaysAgo;
        })()
      }
    };

    const getUnlinkedApVendorCountQuery = {
      VendorId: null,
      ExemptVendorId: null,
      ClientRegisteredVendorId: null,
      isExempt: 0,
      isMissing: 0,
      onHold: 0,
      status: 'Active',
      hasMetaData: { $eq: false },
      ApConfig: {
        options: { required: true }
      }
    };

    const getMisMatchedApVendorCountQuery = {
      options: { subQuery: false },
      ApConfig: {
        options: { required: true },
        attributes: []
      },
      Vendor: {
        attributes: ['id'],
        CompanyVendor: {
          attributes: [],
          Vendor: {
            attributes: []
          }
        }
      },
      isMissing: false,
      literals: {
        $and: 'mismatchQuery'
      }
    };

    const getMissingApVendorCountQuery = {
      ApConfig: {
        options: { required: true },
        attributes: []
      },
      isMissing: true,
      literals: {
        $and: 'missingApVendorIsLinkedVendorsOnlyQuery'
      }
    };

    // The API counts each alert in SQL and returns zeros for a client with no
    // AP config. The query objects above only feed the grid each alert opens.
    from(
      clientDashboardService.getAccountingAlerts({
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS },
        pathParams: { companyId: this.CompanyId }
      })
    ).subscribe({
      next: ({
        viveVendorsNotLinkedToAccounting,
        accountingVendorsAddedLast90Days,
        activeAccountingVendorsUnlinked,
        linkedAccountingVendorsMismatchedStatus,
        missingAccountingVendors
      }) => {
        if (viveVendorsNotLinkedToAccounting > 0) {
          this.accountingAlertsMap.set(
            'VIVE Vendors not Linked to Accounting',
            {
              count: viveVendorsNotLinkedToAccounting,
              route: clientVendorsRoute,
              query: companyVendorsApUnlinkedCountsQuery,
              FilterModel: {
                isApLinked: {
                  filterType: 'set',
                  values: ['0']
                }
              }
            }
          );
        }

        if (accountingVendorsAddedLast90Days > 0) {
          delete getApVendorsAddedLast90DaysQuery.ApConfig['attributes'];
          this.accountingAlertsMap.set(
            'Accounting Vendors Added in Last 90 Days',
            {
              count: accountingVendorsAddedLast90Days,
              route: clientAdminAccountingRoute,
              query: getApVendorsAddedLast90DaysQuery
            }
          );
        }

        if (activeAccountingVendorsUnlinked > 0) {
          this.accountingAlertsMap.set(
            'Active Accounting Vendors Unlinked to VIVE Vendors',
            {
              count: activeAccountingVendorsUnlinked,
              route: clientAdminAccountingRoute,
              query: getUnlinkedApVendorCountQuery,
              FilterModel: {
                isExempt: {
                  filterType: 'set',
                  values: ['0']
                },
                isMissing: {
                  filterType: 'set',
                  values: ['0']
                },
                onHold: {
                  filterType: 'set',
                  values: ['0']
                },
                status: {
                  filterType: 'set',
                  values: ['Active']
                },
                hasMetaData: {
                  filterType: 'set',
                  values: ['0']
                }
              }
            }
          );
        }

        if (linkedAccountingVendorsMismatchedStatus > 0) {
          this.accountingAlertsMap.set(
            'Linked Accounting Vendors with Mismatched Status',
            {
              count: linkedAccountingVendorsMismatchedStatus,
              route: clientAdminAccountingRoute,
              query: getMisMatchedApVendorCountQuery,
              FilterModel: {
                isMissing: {
                  filterType: 'set',
                  values: ['0']
                },
                statusMisMatch: {
                  filterType: 'set',
                  values: ['0']
                }
              }
            }
          );
        }

        if (missingAccountingVendors > 0) {
          this.accountingAlertsMap.set('Missing Accounting Vendors', {
            count: missingAccountingVendors,
            route: clientAdminAccountingRoute,
            query: getMissingApVendorCountQuery,
            FilterModel: {
              isMissing: {
                filterType: 'set',
                values: ['1']
              },
              viveVendorId: {
                filterType: 'text',
                type: 'notEqual',
                filter: ''
              }
            }
          });
        }

        const isApCounts = [
          viveVendorsNotLinkedToAccounting,
          accountingVendorsAddedLast90Days,
          activeAccountingVendorsUnlinked,
          linkedAccountingVendorsMismatchedStatus,
          missingAccountingVendors
        ].some((count) => count > 0);

        if (isApCounts) {
          Object.assign(this.widgetInfo!, { show: true });
        }
        this._cdr.markForCheck();
      },
      error: (err) => {
        throw this.ToastService.addErrorToast('Error', err, {
          returnError: true
        });
      }
    });
  }
}
